/**
 * T-17 (T17-D9) — the ONE place that decides which kind of 422 a compile
 * returned. `POST /submissions/:id/compile` answers 422 with a different body
 * per cause and the clients used to assume a single one (a ValidationGateBlock
 * with a top-level `errors[]`), so a checklist / incomplete / no-confirmed /
 * slot-format block crashed the page with "Cannot read properties of undefined
 * (reading 'length')". PublishWizard, CompileSubmissionPanel and any future
 * consumer classify through this module; none re-derives the shape.
 *
 * Pure and total: any input (including hostile objects with throwing getters)
 * yields a result; an unrecognised or malformed body is `unknown`, carrying the
 * server's message when there is a clean one.
 */
import type { CompileExcludedDocument } from '@/api/v2Client';

export interface GateErrorRow {
  resultId: string;
  ruleName: string;
  targetType: string;
  message: string | null;
}

export interface ChecklistBlockItem {
  id: string;
  label: string;
  failureMessage: string | null;
  /** 'pass' | 'fail' | 'unknown' when the server sent one; null when absent. */
  state: string | null;
}

export type Compile422 =
  | { kind: 'gate'; message: string | null; errors: GateErrorRow[] }
  | {
      kind: 'incomplete';
      message: string | null;
      excludedCount: number;
      excluded: CompileExcludedDocument[];
    }
  | { kind: 'checklist-auto'; message: string | null; items: ChecklistBlockItem[] }
  | { kind: 'no-confirmed'; message: string | null; excludedCount: number | null }
  | { kind: 'slot-format'; message: string | null; violations: string[] }
  | { kind: 'unknown'; message: string | null };

export type Compile422Kind = Compile422['kind'];

const UNKNOWN: Compile422 = { kind: 'unknown', message: null };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function classify(body: unknown): Compile422 {
  if (!isRecord(body)) return UNKNOWN;
  const code = body['error'];
  const message = str(body['message']);
  if (typeof code !== 'string') return { kind: 'unknown', message };
  const data = body['data'];

  switch (code) {
    case 'ValidationGateBlock': {
      const errors = body['errors'];
      if (!Array.isArray(errors)) break;
      return {
        kind: 'gate',
        message,
        errors: errors.filter(isRecord).map((e, i) => ({
          resultId: str(e['resultId']) ?? `gate-${i}`,
          ruleName: str(e['ruleName']) ?? 'unnamed rule',
          targetType: str(e['targetType']) ?? 'unknown target',
          message: str(e['message']),
        })),
      };
    }
    case 'SUBMIT_BLOCKED_INCOMPLETE': {
      if (!isRecord(data) || !Array.isArray(data['excluded'])) break;
      const excluded = data['excluded'].filter(isRecord).map((d, i) => ({
        documentId: str(d['documentId']) ?? `excluded-${i}`,
        fileName: str(d['fileName']) ?? '(unnamed document)',
        module: str(d['module']),
        reason: d['reason'] === 'unclassified' ? ('unclassified' as const) : ('unconfirmed' as const),
      }));
      const count = data['excludedCount'];
      return {
        kind: 'incomplete',
        message,
        excludedCount: typeof count === 'number' ? count : excluded.length,
        excluded,
      };
    }
    case 'PUBLISH_BLOCKED_CHECKLIST_AUTO': {
      if (!isRecord(data) || !Array.isArray(data['items'])) break;
      return {
        kind: 'checklist-auto',
        message,
        items: data['items'].filter(isRecord).map((it, i) => ({
          id: str(it['id']) ?? `item-${i}`,
          label: str(it['label']) ?? str(it['id']) ?? 'Checklist condition',
          failureMessage: str(it['failureMessage']),
          state: str(it['state']),
        })),
      };
    }
    case 'NoConfirmedDocuments': {
      const count = isRecord(data) ? data['excludedCount'] : undefined;
      return { kind: 'no-confirmed', message, excludedCount: typeof count === 'number' ? count : null };
    }
    case 'SlotFormatViolation': {
      const v = body['violations'];
      if (!Array.isArray(v)) break;
      return { kind: 'slot-format', message, violations: v.filter((x): x is string => typeof x === 'string') };
    }
    default:
      break;
  }
  // Includes the retired PUBLISH_BLOCKED_UNCONFIRMED (no server emitter) and
  // any known code whose body is malformed: honest generic card, no override.
  return { kind: 'unknown', message };
}

/** Classify an already-parsed 422 body. Never throws. */
export function classifyCompile422(body: unknown): Compile422 {
  try {
    return classify(body);
  } catch {
    return UNKNOWN;
  }
}

/** Classify the raw text of a 422 response (e.g. `ApiError.body`). Never throws. */
export function classifyCompile422Text(bodyText: string): Compile422 {
  try {
    return classifyCompile422(JSON.parse(bodyText) as unknown);
  } catch {
    return UNKNOWN;
  }
}
